import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import VideoPlayer from './components/VideoPlayer';
import RhythmGame from './components/RhythmGame';
import Countdown from './components/Countdown';
import Ranking from './components/Ranking';
import EntryForm from './components/EntryForm';
import RoundResult from './components/RoundResult';
import RouletteEvent from './components/RouletteEvent';
import './App.css';
import { GAME_MEDIA } from './config/gameMedia';
import { BEATMAPS } from './config/beatmaps';
import AudioController from './utils/audioController';
import { toKstIso } from './utils/firestoreApi';
import { Timestamp, getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import VideoPlayerIframe from './components/VideoPlayerIframe';
import ComingSoon from './components/ComingSoon';
import EventEnd from './components/EventEnd';

const AppLayout = styled.div`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;

  /* 1) 검정 반투명 오버레이 (맨 위)
     2) BG 이미지 (중간)
     3) 보라색 그라데이션 (맨 아래) */
  background:
    /* overlay as a gradient image */
    linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)),
    /* actual BG image, vertical fit */
    url('/BG.jpeg') center top / auto 100vh no-repeat fixed,
    /* purple gradient fill */
    linear-gradient(150deg, #d3e067 0%, #d3e067 100%);
  
  /* 오버레이만 multiply, 나머지는 normal */
  background-blend-mode: multiply, normal, normal;
`;

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;                     /* top/right/bottom/left 모두 0 */
  z-index: -1;                  /* 뒤로 보내기 */

  /* 이미지+오버레이 */
  background-image: url('/BG.jpeg');
  background-color: rgba(0, 0, 0, 0.3);
  background-blend-mode: multiply; 

  /* 화면 & 스크롤 영역 전체를 꽉 채우기 */
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
`;

const Banner = styled.div`
  width: 90%;
  max-width: 400px;
  height: 80px;
  margin: 0 auto;
  margin-top: calc(var(--vh, 1vh) * 2);
  margin-bottom: calc(var(--vh, 1vh) * 2);
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 80%;
    max-width: 300px;
    height: 60px;
    margin-top: calc(var(--vh, 1vh) * 1);
    margin-bottom: calc(var(--vh, 1vh) * 1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0;
    box-shadow: none;
  }
`;

const MainContent = styled.div`
  flex: 1 1 0;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  /* padding: 16px 0 24px 0; */
  max-width: 500px;
  margin: 0 auto;
  margin-top: 10px;
  position: relative;
  height: calc(var(--vh, 1vh) * 100 - 100px);

  @media (max-width: 768px) {
    max-width: 100vw;
    margin-top: 10px;
  }
`;

const HomeScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 30px;
  max-width: 100%;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const HomeButton = styled.button`
  padding: 15px 30px;
  border: none;
  border-radius: 25px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(45deg, #FF6B6B, #FF8E8E);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.6);
  }
`;

const RankingButton = styled.button`
  padding: 12px 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const BottomSpacer = styled.div`
  width: 100%;
  height: 100px;
  @media (max-width: 768px) {
    height: 100px;
  }
`;

const GameInfoText = styled.p`
  font-size: 0.8rem;
  font-weight: bold;
  margin-bottom: 0;
  margin-top: 50px;
  color: #ffffff;
  text-align: center;
  line-height: 1.5;
`;

function App() {
  const [gameState, setGameState] = useState('home'); // home, video, touch, game, roundResult, ranking, entry, rouletteEvent
  const [videoEnded, setVideoEnded] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [currentGameRound, setCurrentGameRound] = useState(0);
  const [gameResults, setGameResults] = useState([]);
  const [userInfo, setUserInfo] = useState(null); // 본인 정보 저장
  const [serverTime, setServerTime] = useState(new Date()); // 즉시 현재 시간으로 시작
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태 제거
  const [isTestMode, setIsTestMode] = useState(false); // 개발자 테스트 모드
  const currentTimeRef = useRef(null);
  const videoForcePlayRef = useRef(null);
  const rhythmGameRef = useRef(null);
  const audioControllerRef = useRef(new AudioController());
  

  
  // audioController를 매번 새로 가져와서 사용 (ref 변경 방지)

  // 로컬스토리지에서 기존 게임 결과 불러오기
  useEffect(() => {
    try {
      const savedGameResults = localStorage.getItem('gameResults');
      if (savedGameResults) {
        const parsedResults = JSON.parse(savedGameResults);
        setGameResults(parsedResults);
        //console.log('로컬스토리지에서 게임 결과를 불러왔습니다:', parsedResults);
      }
    } catch (error) {
      //console.error('로컬스토리지에서 게임 결과 불러오기 실패:', error);
    }
  }, []);

  // 서버 시간 가져오기 (Firebase Firestore 서버 타임스탬프 사용)
  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        // Firebase Firestore 서버 타임스탬프 사용
        const db = getFirestore();
        const tempDocRef = doc(db, 'temp, rTime');
        
        // 서버 타임스탬프로 문서 생성/업데이트
        await setDoc(tempDocRef, { timestamp: serverTimestamp() });
        
        // 생성된 문서에서 서버 시간 가져오기
        const docSnap = await getDoc(tempDocRef);
        if (docSnap.exists()) {
          const serverDate = docSnap.data().timestamp.toDate();
          // Firebase 서버 시간(UTC)을 한국시간(KST)으로 변환
          const kstDate = new Date(serverDate.getTime() + 960* 1000);
          setServerTime(kstDate);
        }
      } catch (error) {
        //console.warn('Firebase 서버 시간 가져오기 실패, 현재 시간 사용:', error);
        setServerTime(new Date());
      }
    };

    fetchServerTime();
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  
  // iOS 크롬 감지 함수
  const isChromeOnIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && /CriOS/i.test(navigator.userAgent);
  };

  // 카운트다운 음악 재생 핸들러
  const handlePlayTouch = async () => {
    const audio = audioControllerRef.current;
    
    try {
      // iOS에서 더 안정적인 오디오 초기화
      if (!audio.audioElement) {
        await audio.init('/PURPLEKISS.mp3');
      }
      
      // iOS에서는 unlock을 더 확실하게 수행
      await audio.unlock();
      
      // iOS에서 추가 대기 시간 (크롬에서는 더 길게)
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        const waitTime = isChromeOnIOS() ? 400 : 200;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    } catch (error) {
      console.warn('Audio initialization error:', error);
    }
  };

  /// 카운트다운 음악 재생 핸들러
  const handleCountdownEnd = async () => {
    const audio = audioControllerRef.current;
    
    try {
      //console.log('카운트다운 종료 - 오디오 재생 시작');
      
      // iOS에서 더 안정적인 재생을 위한 추가 처리
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS에서는 재생 전에 약간의 지연 (크롬에서는 더 길게)
        const waitTime = isChromeOnIOS() ? 200 : 100;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // (1) 카운트다운 끝나면 바로 음악 재생
      await audio.play();
      
      // (2) currentTimeRef 설정
      currentTimeRef.current = () => audio.getCurrentTime();
      
      //console.log('카운트다운 후 오디오 재생 성공');
      
      // (3) 음악 재생이 성공하면 곧바로 게임 화면으로 전환
      setGameState('game');
    } catch (err) {
      //console.error('카운트다운 후 오디오 재생 실패:', err);
      
      // iOS에서 재생 실패 시 재시도 (크롬에서는 더 긴 대기)
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          const retryWaitTime = isChromeOnIOS() ? 1000 : 500;
          await new Promise(resolve => setTimeout(resolve, retryWaitTime));
          await audio.play();
          currentTimeRef.current = () => audio.getCurrentTime();
          setGameState('game');
          return;
        } catch (retryErr) {
          //console.warn('Audio retry failed:', retryErr);
        }
      }
      
      // 음성 재생에 실패하면 홈으로 이동
      setGameState('home');
    }
  };


  const handleVideoEnd = () => {
    //console.log('handleVideoEnd 호출 - gameState를 touch로 변경');
    setVideoEnded(true);
    setGameState('touch');
  };

  const handleGameEnd = (result) => {
    const currentMedia = GAME_MEDIA[currentGameRound] || GAME_MEDIA[0];
    const roundResult = {
      ...result,
      round: currentGameRound + 1,
      modeKey: currentMedia.modeKey,
      modeDisplayName: currentMedia.modeDisplayName,
      maxScore: currentMedia.maxScore || (currentMedia.modeKey === 'hard' ? 4000 : 3000)
    };

    // 기존 코드 주석처리 (라운드 결과 저장 방식 변경)
    /*
    if (currentGameRound < 2) {
      setGameResult(roundResult);
      setGameState('roundResult');
    } else {
      const totalScore = newGameResults.reduce((sum, result) => sum + result.score, 0);
      const totalCombo = newGameResults.reduce((sum, result) => sum + result.combo, 0);
      const maxTotalScore = GAME_MEDIA.reduce((sum, m) => sum + (m.maxScore || (m.modeKey === 'hard' ? 4000 : 3000)), 0);

      const finalResult = {
        totalScore,
        totalCombo,
        maxTotalScore,
        roundResults: newGameResults,
        timestamp: toKstIso(Timestamp.now())
      };

      setGameResult(finalResult);
      setGameState('entry');
    }
    */

    // 현재 메모리의 gameResults에 새 결과 추가
    const newGameResults = [...gameResults, roundResult];
    setGameResults(newGameResults);
    // 로컬스토리지에 저장 (현재 게임의 모든 라운드 결과)
    try {
      localStorage.setItem('gameResults', JSON.stringify(newGameResults));
      //console.log('게임 결과를 로컬스토리지에 저장했습니다:', newGameResults);
    } catch (error) {
      //console.error('로컬스토리지 저장 실패:', error);
    }

    if (currentGameRound < 2) {
      setGameResult(roundResult);
      setGameState('roundResult');
    } else {
      // 마지막 라운드: 로컬스토리지에서 모든 라운드 결과를 가져와서 Firebase에 저장
      try {
        const savedResults = localStorage.getItem('gameResults');
        if (savedResults) {
          const allRoundResults = JSON.parse(savedResults);
          //console.log('마지막 라운드: 로컬스토리지에서 모든 라운드 결과를 가져왔습니다:', allRoundResults);
          
          const totalScore = allRoundResults.reduce((sum, result) => sum + result.score, 0);
          const totalCombo = allRoundResults.reduce((sum, result) => sum + result.combo, 0);
          const maxTotalScore = GAME_MEDIA.reduce((sum, m) => sum + (m.maxScore || (m.modeKey === 'hard' ? 4000 : 3000)), 0);

          const finalResult = {
            totalScore,
            totalCombo,
            maxTotalScore,
            roundResults: allRoundResults, // 로컬스토리지에 저장된 모든 라운드 결과
            timestamp: toKstIso(Timestamp.now())
          };

          setGameResult(finalResult);
          setGameState('entry');
        } else {
          //console.error('로컬스토리지에서 게임 결과를 찾을 수 없습니다.');
        }
      } catch (error) {
        //console.error('마지막 라운드 결과 처리 실패:', error);
      }
    }
  };
  /// 게임하기 버튼 클릭 시 초기화 및 게임 시작
  const handleStartGame = () => {
    setCurrentGameRound(0);
    setGameResults([]);
    // 로컬스토리지 초기화
    try {
      localStorage.removeItem('gameResults');
      //console.log('로컬스토리지 게임 결과를 초기화했습니다.');
    } catch (error) {
      //console.error('로컬스토리지 초기화 실패:', error);
    }
    setGameState('video');
    setVideoEnded(false);
  };

  const handleShowRanking = () => {
    setGameState('ranking');
  };

  const handleBackToHome = () => {
    setGameState('home');
  };

  const handleRetryGame = () => {
    setCurrentGameRound(0);
    setGameResults([]);
    // 로컬스토리지 초기화
    try {
      localStorage.removeItem('gameResults');
      //console.log('로컬스토리지 게임 결과를 초기화했습니다.');
    } catch (error) {
      //console.error('로컬스토리지 초기화 실패:', error);
    }
    setGameState('video');
    setVideoEnded(false);
  };

  const handleEntrySubmit = (submissionData) => {
    // 본인 정보 저장
    setUserInfo({
      nickname: submissionData.userNickname,
      phone: submissionData.userPhone, 
      userDocId: submissionData.userDocId
    });
    
    // 당첨 여부에 따라 다른 화면으로 이동
    if (submissionData.hasWon) {
      // 이미 당첨자인 경우 바로 랭킹으로
      setGameState('ranking');
    } else {
      // 당첨자가 아닌 경우 룰렛으로
      setGameState('rouletteEvent');
    }
  };

  const handleRouletteComplete = () => {
    setGameState('ranking');
  };

  const handleEntryBack = () => {
    setGameState('home');
  };

  const handleLevelUp = () => {
    setCurrentGameRound(currentGameRound + 1);
    setGameState('video');
    setVideoEnded(false);
  };

  const currentMedia = GAME_MEDIA[currentGameRound] || GAME_MEDIA[0];

  // 서버 시간 기준으로 준비중 화면 표시 여부 결정
  // if (isLoading) {
  //   return <div>Loading...</div>; // 또는 로딩 스피너
  // }

  // 즉시 현재 시간으로 판단 (서버 시간이 도착하면 자동 업데이트)
  // serverTime이 이미 한국시간(KST)으로 변환되어 있음
  const kstNow = serverTime;
  const kstNoon = new Date(kstNow);
  kstNoon.setHours(18, 0, 0, 0);
  
  // 개발자 테스트 모드가 아니고, 시간이12:45전이면 준비중 화면 표시
  //if (!isTestMode && kstNow < kstNoon) {
  if (!isTestMode) {
    /// 이벤트 준비중 화면 표시
    // return <ComingSoon onStartGame={() => {
    //   setIsTestMode(true); // 테스트 모드 활성화
    //   setGameState('rouletteEvent'); // home, video, touch, game, roundResult, ranking, entry, rouletteEvent
    // }} />;

    /// 이벤트 종료 화면 표시 
    return <EventEnd />;
  }

  return (
    <AppLayout>
      {/* <BackgroundImage> */}
      <Banner>
        <img src="/PURPLEKISS_Logo.svg" alt="PURPLE K!SS" />
      </Banner>
      <MainContent>
        {gameState === 'touch' && (
            <Countdown onCountdownEnd={handleCountdownEnd} onPlayTouch={handlePlayTouch} round={currentGameRound + 1} />
          )}
        {(gameState === 'video') && (
          // <VideoPlayer
          //   onVideoEnd={handleVideoEnd}
          //   onMiniSize={() => {
          //     if (gameState === 'video') setGameState('touch');
          //   }}
          //   forceMinimized={gameState === 'game'}
          //   videoUrl={currentMedia.videoUrl}
          //   // onForcePlay={(forcePlayFn) => {
          //   //   videoForcePlayRef.current = forcePlayFn;
          //   // }}
          // />

          /// 유튜브 Iframe 버전
          <VideoPlayerIframe
            videoUrl={currentMedia.videoUrl}
            onVideoEnd={handleVideoEnd}
          />
        )}
        {gameState === 'home' && (
          <HomeScreen>
            <GameInfoText>총 3라운드의 게임 후 <br />기프티콘 룰렛과 에어팟 프로2 리워드에 응모가 가능합니다.</GameInfoText> 
            <HomeButton onClick={handleStartGame}>🎵 게임 시작하기</HomeButton>
            <RankingButton onClick={handleShowRanking}>🏆 랭킹 보기</RankingButton>
          </HomeScreen>
        )}

        {gameState === 'game' && (
          <RhythmGame
            ref={rhythmGameRef}
            onGameEnd={handleGameEnd}
            currentTimeRef={currentTimeRef}
            gameMode={currentMedia.modeKey}
            currentRound={currentGameRound + 1}
            totalRounds={GAME_MEDIA.length}
            music={currentMedia.music}
            beatmap={BEATMAPS[currentMedia.modeKey]}
            audioController={audioControllerRef.current}
          />
        )}
        {gameState === 'roundResult' && (
          <RoundResult
            currentRound={currentGameRound + 1}
            result={gameResult}
            onLevelUp={handleLevelUp}
          />
        )}
        {gameState === 'ranking' && (
          <Ranking onBack={handleBackToHome} onRetry={handleRetryGame} userInfo={userInfo} />
        )}
        {gameState === 'entry' && (
          <EntryForm
            gameResult={gameResult}
            onSubmit={handleEntrySubmit}
            onRetry={handleRetryGame}
          />
        )}
        {gameState === 'rouletteEvent' && (
          <RouletteEvent userInfo={userInfo} onComplete={handleRouletteComplete} />
        )}
      </MainContent>
      <BottomSpacer />
      {/* </BackgroundImage> */}
    </AppLayout>
  );
}

export default App;
